import React from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  Check as CheckIcon,
} from "@mui/icons-material";

/**
 * PageActions component - provides navigation and save buttons for the page stepper
 * @param {Object} props
 * @param {number} props.activePage - Current active page index
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPrevious - Callback for previous button
 * @param {Function} props.onNext - Callback for next button
 * @param {Function} props.onSave - Callback for save button
 * @param {boolean} props.canGoNext - Whether next navigation is allowed
 * @param {boolean} props.canGoPrevious - Whether previous navigation is allowed
 * @param {boolean} props.isLastPage - Whether this is the last page
 * @param {boolean} props.isSaving - Whether save operation is in progress
 * @param {boolean} props.hasUnsavedChanges - Whether there are unsaved changes
 * @param {boolean} props.currentPageComplete - Whether current page is complete
 */
const PageActions = ({
  activePage = 0,
  totalPages = 0,
  onPrevious,
  onNext,
  onSave,
  canGoNext = true,
  canGoPrevious = true,
  isLastPage = false,
  isSaving = false,
  hasUnsavedChanges = false,
  currentPageComplete = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handlePrevious = () => {
    if (canGoPrevious && onPrevious) {
      onPrevious();
    }
  };

  const handleNext = () => {
    if (canGoNext && onNext) {
      onNext();
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const getNextButtonText = () => {
    if (isLastPage) {
      return "Save";
    }
    return "Next";
  };

  const getNextButtonIcon = () => {
    if (isLastPage) {
      return <SaveIcon />;
    }
    return <NavigateNextIcon />;
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mt: 3,
        p: 2,
        bgcolor: theme.palette.grey[50],
        borderRadius: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      {/* Left side - Previous button */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {activePage > 0 ? (
          isMobile ? (
            <Tooltip title="Previous Page">
              <IconButton
                onClick={handlePrevious}
                disabled={!canGoPrevious}
                color="primary"
                sx={{
                  bgcolor: "background.paper",
                  border: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                  },
                }}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              sx={{
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              Previous
            </Button>
          )
        ) : (
          <Box /> // Empty box to maintain spacing
        )}
      </Box>

      {/* Center - Page indicator and save button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexGrow: 1,
          justifyContent: "center",
          ...(isMobile && {
            order: 3,
            width: "100%",
            justifyContent: "space-between",
            mt: 1,
          }),
        }}
      >
        {/* Page indicator */}
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: "background.paper",
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: isMobile ? "auto" : 120,
            textAlign: "center",
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: "0.875rem",
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            Page {activePage + 1} of {totalPages}
          </Box>
        </Box>

        {/* Save button */}
        {hasUnsavedChanges && !isLastPage && (
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            size={isMobile ? "small" : "medium"}
            sx={{
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.main,
              "&:hover": {
                borderColor: theme.palette.warning.dark,
                bgcolor: theme.palette.warning.light + "20",
              },
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        )}
      </Box>

      {/* Right side - Next button */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title={getNextButtonText()}>
            <IconButton
              onClick={handleNext}
              disabled={!canGoNext}
              color={isLastPage ? "success" : "primary"}
              sx={{
                bgcolor: isLastPage
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
                color: "white",
                "&:hover": {
                  bgcolor: isLastPage
                    ? theme.palette.success.dark
                    : theme.palette.primary.dark,
                },
                "&:disabled": {
                  bgcolor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {getNextButtonIcon()}
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            endIcon={getNextButtonIcon()}
            onClick={handleNext}
            disabled={!canGoNext}
            color={isLastPage ? "success" : "primary"}
            sx={{
              minWidth: 120,
              ...(isLastPage && {
                bgcolor: theme.palette.success.main,
                "&:hover": {
                  bgcolor: theme.palette.success.dark,
                },
              }),
            }}
          >
            {getNextButtonText()}
          </Button>
        )}
      </Box>

      {/* Mobile-specific layout adjustments */}
      {isMobile && hasUnsavedChanges && (
        <Box sx={{ width: "100%", mt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
            fullWidth
            sx={{
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.main,
              "&:hover": {
                borderColor: theme.palette.warning.dark,
                bgcolor: theme.palette.warning.light + "20",
              },
            }}
          >
            {isSaving ? "Saving..." : "Save Progress"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PageActions;
